import { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../services/api";

function fmt(n, dec = 2) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: dec, maximumFractionDigits: dec }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const TABS = [
  { id: "journal",          label: "Journal",            icon: "📒" },
  { id: "balance",          label: "Balance",            icon: "⚖️" },
  { id: "bilan",            label: "Bilan & P&L",        icon: "📊" },
  { id: "grand-livre",      label: "Grand livre",        icon: "📖" },
  { id: "lettrage",         label: "Lettrage",           icon: "🔗" },
  { id: "balance-agee",     label: "Balance âgée",       icon: "📅" },
  { id: "periodes",         label: "Périodes",           icon: "🗓️" },
  { id: "provisions",       label: "Provisions",         icon: "📑" },
  { id: "sante",            label: "Santé comptable",    icon: "💚" },
  { id: "cca",              label: "CCA",                icon: "👤" },
  { id: "loans",            label: "Prêts bancaires",    icon: "🏦" },
  { id: "discounts",        label: "Escomptes",          icon: "💸" },
  { id: "bank-discounting", label: "Escompte bancaire",  icon: "🏛️" },
];

// ════════════════════════════════════════════════════════════════════════
// Grand Livre
// ════════════════════════════════════════════════════════════════════════
function GrandLivreTab({ currentCompanyId }) {
  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [expanded, setExpanded]     = useState({});
  const [filters, setFilters]       = useState({ account_code: "", journal: "", from: "", to: "" });

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.transactions.grandLivre(filters)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, filters]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (code) => setExpanded(p => ({ ...p, [code]: !p[code] }));

  const exportCSV = (acct) => {
    const cols = ["Date","Journal","Pièce","Libellé","Débit","Crédit","Solde"];
    const rows = acct.lines.map(l => [l.date, l.journal, l.piece || "", l.label, l.debit || 0, l.credit || 0, l.solde.toFixed(2)]);
    const csv = [cols, ...rows].map(r => r.join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    Object.assign(document.createElement("a"), { href: url, download: `grand-livre-${acct.code}.csv` }).click();
  };

  const setFilter = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input className="input" placeholder="Compte (ex: 411)" style={{ maxWidth: 130 }}
          value={filters.account_code} onChange={e => setFilter("account_code", e.target.value)} />
        <select className="input" style={{ maxWidth: 110 }} value={filters.journal} onChange={e => setFilter("journal", e.target.value)}>
          <option value="">Tous journaux</option>
          {["VT","AC","BQ","CA","OD","AN"].map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <input className="input" type="date" style={{ maxWidth: 145 }} value={filters.from} onChange={e => setFilter("from", e.target.value)} />
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>→</span>
        <input className="input" type="date" style={{ maxWidth: 145 }} value={filters.to} onChange={e => setFilter("to", e.target.value)} />
        <button className="btn primary" onClick={load}>🔍 Afficher</button>
        <button className="btn" onClick={() => setExpanded(Object.fromEntries(data.map(a => [a.code, true])))}>Tout ouvrir</button>
        <button className="btn" onClick={() => setExpanded({})}>Tout fermer</button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>Chargement…</div>
      ) : data.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📖</div>
          <div>Aucune écriture trouvée. Ajustez les filtres.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.map(acct => (
            <div key={acct.code} className="card" style={{ padding: 0, overflow: "hidden" }}>
              {/* Entête compte */}
              <div
                onClick={() => toggleExpand(acct.code)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 16px", cursor: "pointer", background: expanded[acct.code] ? "#f0f7ff" : "#f8fafc",
                  borderBottom: expanded[acct.code] ? "1px solid #dbeafe" : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, transform: expanded[acct.code] ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform 0.15s" }}>▶</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#185fa5" }}>{acct.code}</span>
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{acct.name}</span>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>({acct.lines.length} écritures)</span>
                </div>
                <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>D : <strong style={{ color: "#e24b4a" }}>{fmt(acct.total_debit)} €</strong></span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>C : <strong style={{ color: "#1a9e72" }}>{fmt(acct.total_credit)} €</strong></span>
                  <span style={{ fontSize: 12, fontWeight: 700,
                    color: acct.total_debit - acct.total_credit >= 0 ? "#e24b4a" : "#1a9e72" }}>
                    Solde : {fmt(Math.abs(acct.total_debit - acct.total_credit))} € {acct.total_debit >= acct.total_credit ? "D" : "C"}
                  </span>
                  <button className="btn" style={{ fontSize: 11 }} onClick={e => { e.stopPropagation(); exportCSV(acct); }}>⬇️ CSV</button>
                </div>
              </div>

              {/* Lignes */}
              {expanded[acct.code] && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                      {["Date","Journal","Pièce","Libellé","Débit","Crédit","Solde","Lett."].map(h => (
                        <th key={h} style={{ padding: "7px 10px", fontWeight: 600, textAlign: h === "Libellé" ? "left" : h === "Date" || h === "Journal" || h === "Pièce" || h === "Lett." ? "left" : "right" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {acct.lines.map(l => (
                      <tr key={l.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 12,
                        background: l.lettering_code ? "#f0fdf4" : "white" }}>
                        <td style={{ padding: "6px 10px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtDate(l.date)}</td>
                        <td style={{ padding: "6px 10px", fontFamily: "monospace", fontSize: 11 }}>{l.journal}</td>
                        <td style={{ padding: "6px 10px", fontSize: 11, color: "#185fa5" }}>{l.piece || "—"}</td>
                        <td style={{ padding: "6px 10px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#e24b4a", fontWeight: l.debit ? 600 : 400 }}>{l.debit ? fmt(l.debit) + " €" : ""}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#1a9e72", fontWeight: l.credit ? 600 : 400 }}>{l.credit ? fmt(l.credit) + " €" : ""}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700,
                          color: l.solde >= 0 ? "#e24b4a" : "#1a9e72" }}>
                          {fmt(Math.abs(l.solde))} € {l.solde >= 0.01 ? "D" : l.solde <= -0.01 ? "C" : ""}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {l.lettering_code && (
                            <span style={{ fontSize: 10, background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>{l.lettering_code}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Lettrage
// ════════════════════════════════════════════════════════════════════════
function LettrageTab({ currentCompanyId }) {
  const [mode, setMode]             = useState("411");
  const [unmatched, setUnmatched]   = useState([]);
  const [lettrages, setLettrages]   = useState([]);
  const [selected, setSelected]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState(null);

  const loadUnmatched = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.lettrage.unmatched({ account_prefix: mode })
      .then(setUnmatched)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, mode]);

  const loadLettrages = useCallback(() => {
    if (!currentCompanyId) return;
    api.lettrage.list({ account_code: mode })
      .then(setLettrages)
      .catch(console.error);
  }, [currentCompanyId, mode]);

  useEffect(() => { setSelected([]); loadUnmatched(); loadLettrages(); }, [loadUnmatched, loadLettrages]);

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleLettrer = async () => {
    if (selected.length < 2) { setMsg({ type: "error", text: "Sélectionnez au moins 2 écritures." }); return; }
    const sample = unmatched.find(u => selected.includes(u.id));
    if (!sample) return;
    try {
      const r = await api.lettrage.create({ transaction_ids: selected, account_code: sample.account_code });
      setMsg({ type: "ok", text: `Lettrage ${r.lettering_code} créé${r.is_balanced ? " (équilibré ✅)" : " (déséquilibré ⚠️)"}` });
      setSelected([]);
      loadUnmatched(); loadLettrages();
    } catch (err) { setMsg({ type: "error", text: err.message }); }
  };

  const handleDelettrer = async (id) => {
    if (!window.confirm("Supprimer ce lettrage ?")) return;
    try {
      await api.lettrage.remove(id);
      setMsg({ type: "ok", text: "Lettrage supprimé." });
      loadUnmatched(); loadLettrages();
    } catch (err) { setMsg({ type: "error", text: err.message }); }
  };

  const selectedTotal = unmatched.filter(u => selected.includes(u.id));
  const totalD = selectedTotal.reduce((s, u) => s + (u.debit  || 0), 0);
  const totalC = selectedTotal.reduce((s, u) => s + (u.credit || 0), 0);
  const balanced = Math.abs(totalD - totalC) < 0.02;

  return (
    <div>
      {/* Toggle 411 / 401 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {[["411", "Clients (411)"], ["401", "Fournisseurs (401)"]].map(([v, l]) => (
          <button key={v} className={`btn${mode === v ? " primary" : ""}`} onClick={() => setMode(v)}>{l}</button>
        ))}
        <button className="btn" onClick={() => { loadUnmatched(); loadLettrages(); }}>🔄 Actualiser</button>
      </div>

      {msg && (
        <div style={{ marginBottom: 12, padding: "8px 14px", borderRadius: 8, fontSize: 13,
          background: msg.type === "ok" ? "#dcfce7" : "#fee2e2", color: msg.type === "ok" ? "#166534" : "#991b1b" }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ float: "right", border: "none", background: "none", cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, alignItems: "flex-start" }}>
        {/* Écritures non lettrées */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Écritures non lettrées</span>
            {selected.length >= 2 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: balanced ? "#1a9e72" : "#e24b4a" }}>
                  {selected.length} sélectionnées · D={fmt(totalD)}€ / C={fmt(totalC)}€ {balanced ? "✅" : "⚠️"}
                </span>
                <button className="btn primary" onClick={handleLettrer}>🔗 Lettrer</button>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                  <th style={{ width: 32, padding: "7px 10px" }}></th>
                  {["Date","Journal","Pièce","Compte","Libellé","Débit","Crédit"].map(h => (
                    <th key={h} style={{ padding: "7px 10px", fontWeight: 600, textAlign: h === "Libellé" ? "left" : ["Débit","Crédit"].includes(h) ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Chargement…</td></tr>
                ) : unmatched.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                    Toutes les écritures sont lettrées ✅
                  </td></tr>
                ) : unmatched.map(u => (
                  <tr key={u.id} onClick={() => toggleSelect(u.id)}
                    style={{ borderTop: "1px solid #f3f4f6", fontSize: 12, cursor: "pointer",
                      background: selected.includes(u.id) ? "#eff6ff" : "white" }}>
                    <td style={{ padding: "7px 10px", textAlign: "center" }}>
                      <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleSelect(u.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: "7px 10px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtDate(u.date)}</td>
                    <td style={{ padding: "7px 10px", fontFamily: "monospace", fontSize: 11 }}>{u.journal}</td>
                    <td style={{ padding: "7px 10px", fontSize: 11, color: "#185fa5" }}>{u.piece || "—"}</td>
                    <td style={{ padding: "7px 10px", fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>{u.account_code}</td>
                    <td style={{ padding: "7px 10px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.label}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", color: "#e24b4a", fontWeight: u.debit ? 600 : 400 }}>{u.debit ? fmt(u.debit) + " €" : ""}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", color: "#1a9e72", fontWeight: u.credit ? 600 : 400 }}>{u.credit ? fmt(u.credit) + " €" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lettrages existants */}
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Lettrages existants</div>
          {lettrages.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--text-muted)", fontSize: 13 }}>
              Aucun lettrage
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {lettrages.map(l => (
                <div key={l.id} className="card" style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 8 }}>{l.lettering_code}</span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>{l.account_code}</span>
                      <span style={{ marginLeft: 6, fontSize: 11 }}>{l.is_balanced ? "✅" : "⚠️"}</span>
                    </div>
                    <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => handleDelettrer(l.id)}>✕</button>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                    {l.journal_entry_ids.length} écritures · D={fmt(l.total_debit)}€ / C={fmt(l.total_credit)}€
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                    {l.lettered_by} · {fmtDate(l.lettered_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Balance âgée
// ════════════════════════════════════════════════════════════════════════
function BalanceAgeeTab({ currentCompanyId }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [view, setView]         = useState("clients");

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.transactions.balanceAgee()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = (view === "clients" ? data.clients : data.suppliers);
    const header = ["Compte","Nom","Total dû","< 30j","30-60j","60-90j","> 90j"];
    const lines = rows.map(r => [r.account_code, r.name, r.total.toFixed(2), r["<30"].toFixed(2), r["30-60"].toFixed(2), r["60-90"].toFixed(2), r[">90"].toFixed(2)]);
    const csv = [header, ...lines].map(r => r.join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    Object.assign(document.createElement("a"), { href: url, download: `balance-agee-${view}-${data.asOf}.csv` }).click();
  };

  const BUCKETS = [["<30","< 30 jours","#1a9e72"], ["30-60","30 – 60 j","#f59e0b"], ["60-90","60 – 90 j","#f97316"], [">90","> 90 jours","#e24b4a"]];

  const rows = data ? (view === "clients" ? data.clients : data.suppliers) : [];
  const totals = rows.reduce((acc, r) => {
    acc.total    += r.total;
    acc["<30"]   += r["<30"];
    acc["30-60"] += r["30-60"];
    acc["60-90"] += r["60-90"];
    acc[">90"]   += r[">90"];
    return acc;
  }, { total: 0, "<30": 0, "30-60": 0, "60-90": 0, ">90": 0 });

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {[["clients","Créances clients"], ["suppliers","Dettes fournisseurs"]].map(([v, l]) => (
          <button key={v} className={`btn${view === v ? " primary" : ""}`} onClick={() => setView(v)}>{l}</button>
        ))}
        <button className="btn" onClick={load}>🔄 Actualiser</button>
        <button className="btn" onClick={exportCSV} disabled={!data}>⬇️ Export CSV</button>
        {data && <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 8 }}>Au {fmtDate(data.asOf)}</span>}
      </div>

      {/* KPI buckets */}
      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
          {BUCKETS.map(([key, label, color]) => (
            <div key={key} className="card" style={{ padding: "12px 16px", borderTop: `3px solid ${color}` }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt(totals[key])} €</div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                {totals.total > 0 ? Math.round(totals[key] / totals.total * 100) : 0}% du total
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Compte</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Nom</th>
              <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700 }}>Total dû</th>
              {BUCKETS.map(([key, label, color]) => (
                <th key={key} style={{ padding: "9px 14px", textAlign: "right", color }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>Chargement…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                {data ? "Aucun solde non lettré ✅" : "Aucune donnée"}
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.account_code} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                <td style={{ padding: "9px 14px", fontFamily: "monospace", fontWeight: 700, color: "#185fa5" }}>{r.account_code}</td>
                <td style={{ padding: "9px 14px" }}>{r.name || "—"}</td>
                <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700 }}>{fmt(r.total)} €</td>
                {BUCKETS.map(([key, , color]) => (
                  <td key={key} style={{ padding: "9px 14px", textAlign: "right",
                    color: r[key] > 0 ? color : "var(--text-muted)", fontWeight: r[key] > 0 ? 700 : 400 }}>
                    {r[key] > 0 ? fmt(r[key]) + " €" : "—"}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 0 && (
              <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc", fontSize: 13, fontWeight: 700 }}>
                <td colSpan={2} style={{ padding: "9px 14px" }}>TOTAL</td>
                <td style={{ padding: "9px 14px", textAlign: "right" }}>{fmt(totals.total)} €</td>
                {BUCKETS.map(([key, , color]) => (
                  <td key={key} style={{ padding: "9px 14px", textAlign: "right", color }}>{fmt(totals[key])} €</td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Loan amortization (client-side) ────────────────────────────────────
function computeAmortization(loan) {
  const monthlyRate = parseFloat(loan.interest_rate) / 100 / 12;
  // Assurance à montant fixe prioritaire si renseignée — sinon calcul en %
  // du capital restant (comportement d'origine, conservé pour compatibilité).
  const hasFixedInsurance = loan.insurance_fixed_amount != null && loan.insurance_fixed_amount !== '';
  const fixedInsurance = hasFixedInsurance ? parseFloat(loan.insurance_fixed_amount) : null;
  const insuranceRate = parseFloat(loan.insurance_rate || 0) / 100 / 12;
  let capital = parseFloat(loan.amount_total);
  const rows = [];
  let d = new Date(loan.start_date + 'T00:00:00');
  let month = 0;
  while (capital > 0.01 && month < 480) {
    month++;
    const interest   = Math.round(capital * monthlyRate * 100) / 100;
    const insurance  = hasFixedInsurance ? fixedInsurance : Math.round(capital * insuranceRate * 100) / 100;
    const principal  = Math.round((parseFloat(loan.monthly_payment) - interest - insurance) * 100) / 100;
    capital = Math.max(0, Math.round((capital - principal) * 100) / 100);
    rows.push({ month, date: d.toISOString().slice(0, 7), payment: parseFloat(loan.monthly_payment), interest, insurance, principal, capital });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return rows;
}

// ════════════════════════════════════════════════════════════════════════
// CCA Tab
// ════════════════════════════════════════════════════════════════════════
function CCATab({ currentCompanyId }) {
  const [associates, setAssociates] = useState([]);
  const [selected, setSelected]     = useState(null);
  const [transactions, setTxs]      = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showTx, setShowTx]         = useState(false);
  const [form, setForm]             = useState({ associate_name: "", associate_type: "gérant", taux_interet: "2.27", account_number: "" });
  const [txForm, setTxForm]         = useState({ date: new Date().toISOString().slice(0, 10), type: "apport", amount: "", description: "" });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [ccaPdfLoadingId, setCcaPdfLoadingId] = useState(null);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.accounting.listCCA(currentCompanyId).then(setAssociates).catch(console.error);
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const selectCCA = (a) => {
    setSelected(a);
    api.accounting.ccaTransactions(a.id).then(setTxs).catch(console.error);
  };

  const reloadTx = (id) => api.accounting.ccaTransactions(id).then(setTxs).catch(console.error);

  const handleCreate = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      await api.accounting.createCCA({ ...form, company_id: currentCompanyId, taux_interet: parseFloat(form.taux_interet) });
      setShowCreate(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleAddTx = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      await api.accounting.addCCATx(selected.id, { ...txForm, company_id: currentCompanyId, amount: parseFloat(txForm.amount) });
      setShowTx(false);
      setTxForm({ date: new Date().toISOString().slice(0, 10), type: "apport", amount: "", description: "" });
      reloadTx(selected.id); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteTx = async (tx) => {
    if (!window.confirm("Supprimer ce mouvement ?")) return;
    await api.accounting.deleteCCATx(selected.id, tx.id);
    reloadTx(selected.id); load();
  };

  const handleInterests = async (a) => {
    const year = window.prompt("Année pour le calcul des intérêts ?", String(new Date().getFullYear()));
    if (!year) return;
    try {
      const r = await api.accounting.computeInterests(a.id, { company_id: currentCompanyId, year: parseInt(year) });
      if (r.interests > 0) alert(`✅ Intérêts ${year} : ${fmt(r.interests)} € comptabilisés.`);
      else alert(r.message || "Aucun intérêt à calculer.");
      load(); if (selected?.id === a.id) reloadTx(a.id);
    } catch (err) { alert(err.message); }
  };

  // Téléchargement PDF CCA avec JWT (remplace l'ancien <a href> non authentifié)
  const handleDownloadCcaPdf = async (e, a) => {
    e.stopPropagation();
    setCcaPdfLoadingId(a.id);
    try {
      const { blob, filename } = await api.accounting.ccaPdfDownload(a.id);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: filename }).click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setCcaPdfLoadingId(null);
    }
  };

  const balance = (a) => a.computed_balance ?? 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "flex-start" }}>
      {/* ─ Colonne gauche : liste associés ─ */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontWeight: 700 }}>Comptes courants</span>
          <button className="btn primary" style={{ fontSize: 12 }} onClick={() => { setShowCreate(true); setError(""); }}>+ Associé</button>
        </div>
        {associates.length === 0 ? (
          <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
            <div style={{ fontSize: 13 }}>Aucun compte courant d'associé</div>
          </div>
        ) : associates.map(a => (
          <div key={a.id} className="card" style={{ marginBottom: 10, cursor: "pointer", border: `2px solid ${selected?.id === a.id ? "var(--g)" : "transparent"}`, transition: "border 0.15s" }} onClick={() => selectCCA(a)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{a.associate_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{a.associate_type} · {a.taux_interet}% · C455{a.account_number || "xxx"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: balance(a) >= 0 ? "#1a9e72" : "#e24b4a" }}>{fmt(balance(a))} €</div>
                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>Solde CCA</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 10 }} onClick={e => e.stopPropagation()}>
              <button className="btn" style={{ fontSize: 11, flex: 1 }} onClick={() => { selectCCA(a); setShowTx(true); setError(""); }}>+ Mouvement</button>
              <button className="btn" style={{ fontSize: 11, flex: 1 }} onClick={() => handleInterests(a)}>📐 Intérêts</button>
              <button className="btn" style={{ fontSize: 11 }} onClick={(e) => handleDownloadCcaPdf(e, a)} disabled={ccaPdfLoadingId === a.id}>
                {ccaPdfLoadingId === a.id ? "⏳" : "📄"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ─ Colonne droite : mouvements ─ */}
      <div>
        {selected ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Mouvements — {selected.associate_name}</div>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              {transactions.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>Aucun mouvement enregistré</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                      <th style={{ padding: "9px 12px", textAlign: "left", fontWeight: 600 }}>Date</th>
                      <th style={{ padding: "9px 12px", textAlign: "left", fontWeight: 600 }}>Type</th>
                      <th style={{ padding: "9px 12px", textAlign: "left", fontWeight: 600 }}>Libellé</th>
                      <th style={{ padding: "9px 12px", textAlign: "right", fontWeight: 600 }}>Débit</th>
                      <th style={{ padding: "9px 12px", textAlign: "right", fontWeight: 600 }}>Crédit</th>
                      <th style={{ padding: "9px 12px", fontWeight: 600 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => {
                      const isDebit = t.type === 'retrait';
                      return (
                        <tr key={t.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                          <td style={{ padding: "8px 12px", color: "var(--text-muted)" }}>{fmtDate(t.date)}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 700,
                              background: t.type === 'apport' ? '#1a9e7215' : t.type === 'retrait' ? '#e24b4a15' : '#185fa515',
                              color: t.type === 'apport' ? '#1a9e72' : t.type === 'retrait' ? '#e24b4a' : '#185fa5',
                            }}>{t.type}</span>
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: 12 }}>{t.description || '—'}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: "#e24b4a", fontWeight: 600 }}>{isDebit ? fmt(t.amount) + ' €' : ''}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: "#1a9e72", fontWeight: 600 }}>{!isDebit ? fmt(t.amount) + ' €' : ''}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <button className="btn" style={{ fontSize: 10, padding: "2px 6px", color: "#e24b4a" }} onClick={() => handleDeleteTx(t)}>🗑</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, fontSize: 12, color: "var(--text-muted)" }}>
              <strong>Écritures PCG :</strong>&nbsp;
              Apport → D&nbsp;512 / C&nbsp;455{selected.account_number || "xxx"} &nbsp;·&nbsp;
              Retrait → D&nbsp;455 / C&nbsp;512 &nbsp;·&nbsp;
              Intérêts → D&nbsp;661 / C&nbsp;455{selected.account_number || "xxx"}
            </div>
          </>
        ) : (
          <div className="card" style={{ textAlign: "center", padding: 64, color: "var(--text-muted)" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>👈</div>
            <div>Sélectionnez un associé pour voir ses mouvements</div>
          </div>
        )}
      </div>

      {/* Modal créer associé */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: "100%", maxWidth: 420 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Nouvel associé CCA</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowCreate(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom de l'associé *</label>
                <input className="input" required value={form.associate_name} onChange={e => setForm(f => ({ ...f, associate_name: e.target.value }))} placeholder="Prénom Nom" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Qualité</label>
                  <select className="input" value={form.associate_type} onChange={e => setForm(f => ({ ...f, associate_type: e.target.value }))}>
                    <option value="gérant">Gérant</option>
                    <option value="associé">Associé</option>
                    <option value="actionnaire">Actionnaire</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux intérêt (%)</label>
                  <input className="input" type="number" step="0.01" value={form.taux_interet} onChange={e => setForm(f => ({ ...f, taux_interet: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Suffixe compte 455 (ex : 001)</label>
                <input className="input" value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} placeholder="001" />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "…" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal mouvement */}
      {showTx && selected && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: "100%", maxWidth: 420 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Mouvement — {selected.associate_name}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowTx(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleAddTx}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date *</label>
                  <input className="input" type="date" required value={txForm.date} onChange={e => setTxForm(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Type *</label>
                  <select className="input" value={txForm.type} onChange={e => setTxForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="apport">Apport (C 455)</option>
                    <option value="retrait">Retrait (D 455)</option>
                    <option value="intérêts">Intérêts (D 661)</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant (€) *</label>
                <input className="input" type="number" step="0.01" min="0.01" required value={txForm.amount} onChange={e => setTxForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Libellé</label>
                <input className="input" value={txForm.description} onChange={e => setTxForm(f => ({ ...f, description: e.target.value }))} placeholder="Apport de trésorerie…" />
              </div>
              {txForm.type && txForm.amount && (
                <div style={{ padding: "8px 12px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
                  Écriture : {txForm.type === 'retrait' ? `D 455${selected.account_number || 'xxx'} / C 512` : txForm.type === 'intérêts' ? `D 661 / C 455${selected.account_number || 'xxx'}` : `D 512 / C 455${selected.account_number || 'xxx'}`} — {parseFloat(txForm.amount || 0).toFixed(2)} €
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowTx(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "…" : "Enregistrer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Loans Tab
// ════════════════════════════════════════════════════════════════════════
const LOAN_EMPTY = { bank_name: "", loan_reference: "", amount_total: "", interest_rate: "", insurance_rate: "0", insurance_fixed_amount: "", start_date: "", end_date: "", monthly_payment: "", loan_type: "amortissable", guarantee: "" };

function LoansTab({ currentCompanyId }) {
  const [loans, setLoans]           = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [editTarget, setEdit]       = useState(null);
  const [form, setForm]             = useState(LOAN_EMPTY);
  const [selected, setSelected]     = useState(null);
  const [schedule, setSchedule]     = useState([]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [showFull, setShowFull]     = useState(false);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.accounting.loans(currentCompanyId).then(setLoans).catch(console.error);
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) setSchedule(computeAmortization(selected));
  }, [selected]);

  const openCreate = () => { setEdit(null); setForm(LOAN_EMPTY); setError(""); setShowModal(true); };
  const openEdit   = (l) => { setEdit(l); setForm({ bank_name: l.bank_name, loan_reference: l.loan_reference || "", amount_total: String(l.amount_total), interest_rate: String(l.interest_rate), insurance_rate: String(l.insurance_rate || 0), insurance_fixed_amount: l.insurance_fixed_amount != null ? String(l.insurance_fixed_amount) : "", start_date: l.start_date, end_date: l.end_date, monthly_payment: String(l.monthly_payment), loan_type: l.loan_type, guarantee: l.guarantee || "" }); setError(""); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, company_id: currentCompanyId, amount_total: parseFloat(form.amount_total), amount_remaining: parseFloat(form.amount_total), interest_rate: parseFloat(form.interest_rate), insurance_rate: parseFloat(form.insurance_rate || 0), insurance_fixed_amount: form.insurance_fixed_amount !== "" ? parseFloat(form.insurance_fixed_amount) : null, monthly_payment: parseFloat(form.monthly_payment) };
      if (editTarget) await api.accounting.updateLoan(editTarget.id, payload);
      else await api.accounting.createLoan(payload);
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce prêt ?")) return;
    await api.accounting.deleteLoan(id).catch(console.error);
    if (selected?.id === id) { setSelected(null); setSchedule([]); }
    load();
  };

  // Solde restant dû — priorité au solde RÉEL (calculé backend depuis les
  // vraies écritures comptabilisées, reflète les décalages de prélèvement).
  // Repli sur la projection théorique uniquement si le prêt n'a pas encore
  // de suivi comptable configuré (accounting_account_code non renseigné).
  function computeRemainingToday(loan) {
    if (loan.has_real_tracking && loan.amount_remaining_real != null) {
      return loan.amount_remaining_real;
    }
    if (!loan.is_active && loan.amount_remaining != null) {
      return loan.amount_remaining;
    }
    const schedule = computeAmortization(loan);
    if (schedule.length === 0) return loan.amount_remaining ?? loan.amount_total;
    const todayKey = new Date().toISOString().slice(0, 7);
    const pastOrCurrent = schedule.filter(r => r.date <= todayKey);
    if (pastOrCurrent.length === 0) return loan.amount_total;
    return pastOrCurrent[pastOrCurrent.length - 1].capital;
  }

  const totalRemaining = loans.filter(l => l.is_active).reduce((s, l) => s + computeRemainingToday(l), 0);
  const totalPayments  = loans.filter(l => l.is_active).reduce((s, l) => s + l.monthly_payment, 0);

  const chartData = selected && schedule.length > 0
    ? schedule.filter((_, i) => i % 3 === 0 || i === schedule.length - 1).map(r => ({ month: r.date, capital: r.capital, intérêts: r.interest }))
    : [];

  const displaySchedule = showFull ? schedule : schedule.slice(0, 24);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16 }}>
          <div className="kpi" style={{ minWidth: 160 }}>
            <div className="kpi-label">Capital restant dû</div>
            <div className="kpi-value" style={{ color: "#e24b4a" }}>{fmt(totalRemaining)} €</div>
          </div>
          <div className="kpi" style={{ minWidth: 160 }}>
            <div className="kpi-label">Mensualités totales</div>
            <div className="kpi-value" style={{ color: "#ba7517" }}>{fmt(totalPayments)} €/mois</div>
          </div>
        </div>
        <button className="btn primary" onClick={openCreate}>+ Nouveau prêt</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "380px 1fr" : "1fr", gap: 16, alignItems: "flex-start" }}>
        {/* Liste des prêts */}
        <div>
          {loans.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🏦</div>
              <div>Aucun prêt enregistré</div>
            </div>
          ) : loans.map(l => (
            <div key={l.id} className="card" style={{ marginBottom: 10, cursor: "pointer", border: `2px solid ${selected?.id === l.id ? "var(--g)" : "transparent"}` }} onClick={() => setSelected(l)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{l.bank_name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{l.loan_reference || "—"} · {l.loan_type}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#e24b4a" }}>{fmt(computeRemainingToday(l))} €</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{fmt(l.amount_total)} € initial</div>
                  <div style={{ marginTop: 3 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8,
                      background: l.has_real_tracking ? "#dcfce7" : "#fef3c7",
                      color: l.has_real_tracking ? "#166534" : "#92400e",
                    }}>
                      {l.has_real_tracking ? "✓ Suivi réel" : "≈ Estimation"}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: "var(--text-muted)" }}>
                <span>Taux {l.interest_rate}% + assurance {l.insurance_rate || 0}%</span>
                <span style={{ fontWeight: 600, color: "#ba7517" }}>{fmt(l.monthly_payment)} €/mois</span>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                <button className="btn" style={{ fontSize: 11 }} onClick={() => openEdit(l)}>✏️ Modifier</button>
                <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => handleDelete(l.id)}>🗑 Supprimer</button>
              </div>
            </div>
          ))}
        </div>

        {/* Tableau d'amortissement */}
        {selected && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Amortissement — {selected.bank_name}</div>
            {chartData.length > 1 && (
              <div className="card" style={{ marginBottom: 14, padding: "16px 8px" }}>
                <div style={{ fontWeight: 600, marginBottom: 8, paddingLeft: 8, fontSize: 13 }}>Capital restant dû (€)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} tickCount={8} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmt(v) + ' €'} />
                    <Legend />
                    <Line type="monotone" dataKey="capital" stroke="#185fa5" strokeWidth={2} dot={false} name="Capital restant" />
                    <Line type="monotone" dataKey="intérêts" stroke="#e24b4a" strokeWidth={1.5} dot={false} name="Intérêts mensuel" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Mois</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Échéance</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Capital</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Intérêts</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Assurance</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Restant</th>
                  </tr>
                </thead>
                <tbody>
                  {displaySchedule.map(r => (
                    <tr key={r.month} style={{ borderTop: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 10px", color: "var(--text-muted)" }}>{r.date}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(r.payment)} €</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#1a9e72" }}>{fmt(r.principal)} €</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#e24b4a" }}>{fmt(r.interest)} €</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#ba7517" }}>{fmt(r.insurance)} €</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(r.capital)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {schedule.length > 24 && (
                <div style={{ textAlign: "center", padding: "8px 0", borderTop: "1px solid #f3f4f6" }}>
                  <button className="btn" style={{ fontSize: 12 }} onClick={() => setShowFull(f => !f)}>
                    {showFull ? `▲ Réduire` : `▼ Voir les ${schedule.length} échéances`}
                  </button>
                </div>
              )}
            </div>
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, fontSize: 12, color: "var(--text-muted)" }}>
              <strong>PCG :</strong> Souscription → D&nbsp;512 / C&nbsp;164 &nbsp;·&nbsp;
              Remboursement capital → D&nbsp;164 / C&nbsp;512 &nbsp;·&nbsp;
              Intérêts → D&nbsp;661 / C&nbsp;512
            </div>
          </div>
        )}
      </div>

      {/* Modal prêt */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
          <div className="card" style={{ width: "100%", maxWidth: 520 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{editTarget ? "Modifier le prêt" : "Nouveau prêt bancaire"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Banque *</label>
                  <input className="input" required value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="Crédit Agricole, BNP…" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Référence contrat</label>
                  <input className="input" value={form.loan_reference} onChange={e => setForm(f => ({ ...f, loan_reference: e.target.value }))} placeholder="2024-PRET-001" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Type</label>
                  <select className="input" value={form.loan_type} onChange={e => setForm(f => ({ ...f, loan_type: e.target.value }))}>
                    <option value="amortissable">Amortissable</option>
                    <option value="in_fine">In fine</option>
                    <option value="revolving">Revolving</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Capital emprunté (€) *</label>
                  <input className="input" type="number" step="0.01" required value={form.amount_total} onChange={e => setForm(f => ({ ...f, amount_total: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Mensualité (€) *</label>
                  <input className="input" type="number" step="0.01" required value={form.monthly_payment} onChange={e => setForm(f => ({ ...f, monthly_payment: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux intérêt annuel (%) *</label>
                  <input className="input" type="number" step="0.001" required value={form.interest_rate} onChange={e => setForm(f => ({ ...f, interest_rate: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux assurance (%) </label>
                  <input className="input" type="number" step="0.001" value={form.insurance_rate} onChange={e => setForm(f => ({ ...f, insurance_rate: e.target.value }))} disabled={form.insurance_fixed_amount !== ""} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Assurance montant fixe (€/mois)</label>
                  <input className="input" type="number" step="0.01" value={form.insurance_fixed_amount} onChange={e => setForm(f => ({ ...f, insurance_fixed_amount: e.target.value }))} placeholder="Laisser vide pour utiliser le taux %" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de début *</label>
                  <input className="input" type="date" required value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de fin *</label>
                  <input className="input" type="date" required value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Garantie</label>
                  <input className="input" value={form.guarantee} onChange={e => setForm(f => ({ ...f, guarantee: e.target.value }))} placeholder="Hypothèque, caution personnelle…" />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "…" : editTarget ? "Modifier" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Discounts Tab (escomptes commerciaux client + fournisseur)
// ════════════════════════════════════════════════════════════════════════
const DISC_EMPTY = { type: "client", invoice_ref: "", client_name: "", rate: "", amount: "", discount_days: "10", payment_date: "", notes: "" };

function DiscountsTab({ currentCompanyId }) {
  const [discounts, setDiscounts]   = useState([]);
  const [filter, setFilter]         = useState("all");
  const [showModal, setShowModal]   = useState(false);
  const [editTarget, setEdit]       = useState(null);
  const [form, setForm]             = useState(DISC_EMPTY);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.accounting.discounts(currentCompanyId, filter === "all" ? undefined : filter).then(setDiscounts).catch(console.error);
  }, [currentCompanyId, filter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(DISC_EMPTY); setError(""); setShowModal(true); };
  const openEdit = (d) => {
    setEdit(d);
    setForm({ type: d.type, invoice_ref: d.invoice_ref || "", client_name: d.client_name || "", rate: String(d.rate), amount: String(d.amount), discount_days: String(d.discount_days || 10), payment_date: d.payment_date || "", notes: d.notes || "" });
    setError(""); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, company_id: currentCompanyId, rate: parseFloat(form.rate), amount: parseFloat(form.amount || 0), discount_days: parseInt(form.discount_days) };
      if (editTarget) await api.accounting.updateDiscount(editTarget.id, payload);
      else await api.accounting.createDiscount(payload);
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet escompte ?")) return;
    await api.accounting.deleteDiscount(id).catch(console.error); load();
  };

  const totalClient = discounts.filter(d => d.type === 'client').reduce((s, d) => s + (d.amount || 0), 0);
  const totalFourn  = discounts.filter(d => d.type === 'fournisseur').reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 16 }}>
          <div className="kpi" style={{ minWidth: 180 }}>
            <div className="kpi-label">Escomptes accordés (clients)</div>
            <div className="kpi-value" style={{ color: "#e24b4a" }}>{fmt(totalClient)} €</div>
            <div className="kpi-sub">Charge 665</div>
          </div>
          <div className="kpi" style={{ minWidth: 180 }}>
            <div className="kpi-label">Escomptes obtenus (fournisseurs)</div>
            <div className="kpi-value" style={{ color: "#1a9e72" }}>{fmt(totalFourn)} €</div>
            <div className="kpi-sub">Produit 765</div>
          </div>
        </div>
        <button className="btn primary" onClick={openCreate}>+ Escompte</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["all", "Tous"], ["client", "Clients (665)"], ["fournisseur", "Fournisseurs (765)"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "var(--border)", background: filter === id ? "var(--g)" : "#fff", color: filter === id ? "#fff" : "var(--text-muted)" }}>{lbl}</button>
        ))}
      </div>

      {discounts.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>💸</div>
          <div>Aucun escompte enregistré</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Type</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Réf. facture</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Client / Fournisseur</th>
                <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Taux</th>
                <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Montant</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Délai</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Écriture PCG</th>
                <th style={{ padding: "9px 14px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map(d => (
                <tr key={d.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                  <td style={{ padding: "8px 14px" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 700, background: d.type === 'client' ? '#e24b4a15' : '#1a9e7215', color: d.type === 'client' ? '#e24b4a' : '#1a9e72' }}>{d.type}</span>
                  </td>
                  <td style={{ padding: "8px 14px", fontFamily: "monospace", fontWeight: 600, color: "var(--blue)" }}>{d.invoice_ref || '—'}</td>
                  <td style={{ padding: "8px 14px" }}>{d.client_name || '—'}</td>
                  <td style={{ padding: "8px 14px", textAlign: "right" }}>{d.rate}%</td>
                  <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: d.type === 'client' ? '#e24b4a' : '#1a9e72' }}>{fmt(d.amount)} €</td>
                  <td style={{ padding: "8px 14px", color: "var(--text-muted)" }}>{d.discount_days || 0}j</td>
                  <td style={{ padding: "8px 14px", fontSize: 11, color: "#185fa5", fontFamily: "monospace" }}>
                    {d.type === 'client' ? 'D 665 / C 411' : 'D 401 / C 765'}
                  </td>
                  <td style={{ padding: "8px 14px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn" style={{ fontSize: 10 }} onClick={() => openEdit(d)}>✏️</button>
                      <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleDelete(d.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal escompte */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{editTarget ? "Modifier l'escompte" : "Nouvel escompte commercial"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Type *</label>
                  <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="client">Accordé au client (665)</option>
                    <option value="fournisseur">Obtenu fournisseur (765)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Réf. facture</label>
                  <input className="input" value={form.invoice_ref} onChange={e => setForm(f => ({ ...f, invoice_ref: e.target.value }))} placeholder="FA-2024-001" />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>{form.type === 'client' ? 'Nom du client' : 'Nom du fournisseur'}</label>
                  <input className="input" value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Société…" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux (%) *</label>
                  <input className="input" type="number" step="0.01" required value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} placeholder="2.00" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant (€)</label>
                  <input className="input" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Délai paiement (j)</label>
                  <input className="input" type="number" value={form.discount_days} onChange={e => setForm(f => ({ ...f, discount_days: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date paiement effectif</label>
                  <input className="input" type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
                  <input className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observations…" />
                </div>
              </div>
              {form.rate && form.amount && (
                <div style={{ padding: "8px 12px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
                  Écriture : {form.type === 'client' ? `D 665 (${fmt(parseFloat(form.amount || 0))} €) / C 411` : `D 401 / C 765 (${fmt(parseFloat(form.amount || 0))} €)`}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "…" : editTarget ? "Modifier" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Bank Discounting Tab
// ════════════════════════════════════════════════════════════════════════
const STATUS_BD = {
  en_cours: { label: "En cours",  color: "#185fa5", bg: "#185fa515" },
  honoré:   { label: "Honoré",   color: "#1a9e72", bg: "#1a9e7215" },
  impayé:   { label: "Impayé",   color: "#e24b4a", bg: "#e24b4a15" },
  retourné: { label: "Retourné", color: "#ba7517", bg: "#ba751715" },
};
const BD_EMPTY = { invoice_ref: "", client_name: "", bank_name: "", face_value: "", discount_rate: "", commission_fees: "0", remittance_date: "", due_date: "" };

function BankDiscountingTab({ currentCompanyId }) {
  const [records, setRecords]       = useState([]);
  const [filterStatus, setFilter]   = useState("all");
  const [showModal, setShowModal]   = useState(false);
  const [editTarget, setEdit]       = useState(null);
  const [form, setForm]             = useState(BD_EMPTY);
  const [preview, setPreview]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.accounting.bankDiscounting(currentCompanyId, filterStatus === "all" ? undefined : filterStatus).then(setRecords).catch(console.error);
  }, [currentCompanyId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const computePreview = (f) => {
    if (!f.face_value || !f.discount_rate || !f.remittance_date || !f.due_date) return null;
    const fv   = parseFloat(f.face_value);
    const rate = parseFloat(f.discount_rate);
    const comm = parseFloat(f.commission_fees || 0);
    const days = Math.max(1, Math.round((new Date(f.due_date) - new Date(f.remittance_date)) / 86400000));
    const agios = Math.round(fv * (rate / 100) * days / 360 * 100) / 100;
    const net   = Math.round((fv - agios - comm) * 100) / 100;
    const teg   = Math.round(((agios + comm) / net) * (360 / days) * 100 * 100) / 100;
    return { days, agios, net, teg };
  };

  const handleFormChange = (field, val) => {
    const nf = { ...form, [field]: val };
    setForm(nf);
    setPreview(computePreview(nf));
  };

  const openCreate = () => { setEdit(null); setForm(BD_EMPTY); setPreview(null); setError(""); setShowModal(true); };
  const openEdit = (r) => {
    setEdit(r);
    const f = { invoice_ref: r.invoice_ref || "", client_name: r.client_name || "", bank_name: r.bank_name || "", face_value: String(r.face_value), discount_rate: String(r.discount_rate), commission_fees: String(r.commission_fees || 0), remittance_date: r.remittance_date, due_date: r.due_date };
    setForm(f); setPreview(computePreview(f)); setError(""); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, company_id: currentCompanyId, face_value: parseFloat(form.face_value), discount_rate: parseFloat(form.discount_rate), commission_fees: parseFloat(form.commission_fees || 0) };
      if (editTarget) await api.accounting.updateBankDisc(editTarget.id, payload);
      else await api.accounting.createBankDisc(payload);
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleStatus = async (id, status) => {
    await api.accounting.bankDiscStatus(id, status).catch(console.error); load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette remise à l'escompte ?")) return;
    await api.accounting.deleteBankDisc(id).catch(console.error); load();
  };

  const totalFV  = records.filter(r => r.status === 'en_cours').reduce((s, r) => s + r.face_value, 0);
  const totalNet = records.filter(r => r.status === 'en_cours').reduce((s, r) => s + (r.net_advance || r.advance_amount), 0);
  const totalAgios = records.reduce((s, r) => s + (r.agios || r.discount_fees || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <div className="kpi" style={{ minWidth: 160 }}>
            <div className="kpi-label">Effets en portefeuille</div>
            <div className="kpi-value" style={{ color: "#185fa5" }}>{fmt(totalFV)} €</div>
          </div>
          <div className="kpi" style={{ minWidth: 160 }}>
            <div className="kpi-label">Trésorerie mobilisée</div>
            <div className="kpi-value" style={{ color: "#1a9e72" }}>{fmt(totalNet)} €</div>
          </div>
          <div className="kpi" style={{ minWidth: 140 }}>
            <div className="kpi-label">Agios totaux</div>
            <div className="kpi-value" style={{ color: "#e24b4a" }}>{fmt(totalAgios)} €</div>
          </div>
        </div>
        <button className="btn primary" onClick={openCreate}>+ Remise à l'escompte</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["all", "Tous"], ["en_cours", "En cours"], ["honoré", "Honorés"], ["impayé", "Impayés"], ["retourné", "Retournés"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "var(--border)", background: filterStatus === id ? "var(--g)" : "#fff", color: filterStatus === id ? "#fff" : "var(--text-muted)" }}>{lbl}</button>
        ))}
      </div>

      {records.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏛️</div>
          <div>Aucune remise à l'escompte</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                <th style={{ padding: "9px 12px", textAlign: "left" }}>Réf. facture</th>
                <th style={{ padding: "9px 12px", textAlign: "left" }}>Client</th>
                <th style={{ padding: "9px 12px", textAlign: "left" }}>Banque</th>
                <th style={{ padding: "9px 12px", textAlign: "right" }}>Valeur nominale</th>
                <th style={{ padding: "9px 12px", textAlign: "right" }}>Agios</th>
                <th style={{ padding: "9px 12px", textAlign: "right" }}>Net reçu</th>
                <th style={{ padding: "9px 12px", textAlign: "right" }}>Jours</th>
                <th style={{ padding: "9px 12px", textAlign: "right" }}>Taux</th>
                <th style={{ padding: "9px 12px", textAlign: "left" }}>Échéance</th>
                <th style={{ padding: "9px 12px", textAlign: "left" }}>Statut</th>
                <th style={{ padding: "9px 12px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => {
                const sc = STATUS_BD[r.status] || STATUS_BD.en_cours;
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                    <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>{r.invoice_ref || '—'}</td>
                    <td style={{ padding: "8px 12px" }}>{r.client_name || '—'}</td>
                    <td style={{ padding: "8px 12px" }}>{r.bank_name || '—'}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{fmt(r.face_value)} €</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#e24b4a" }}>{fmt(r.agios || r.discount_fees)} €</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#1a9e72", fontWeight: 700 }}>{fmt(r.net_advance || r.advance_amount)} €</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "var(--text-muted)" }}>{r.days_count}j</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "var(--text-muted)" }}>{r.discount_rate}%</td>
                    <td style={{ padding: "8px 12px", color: "var(--text-muted)" }}>{fmtDate(r.due_date)}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {r.status === 'en_cours' && (
                          <>
                            <button className="btn" style={{ fontSize: 10, color: "#1a9e72" }} onClick={() => handleStatus(r.id, 'honoré')}>✓ Honoré</button>
                            <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleStatus(r.id, 'impayé')}>✗ Impayé</button>
                          </>
                        )}
                        <button className="btn" style={{ fontSize: 10 }} onClick={() => openEdit(r)}>✏️</button>
                        <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleDelete(r.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, padding: "10px 14px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, fontSize: 12, color: "var(--text-muted)" }}>
        <strong>Flux PCG :</strong> Remise → D&nbsp;519 / C&nbsp;411 &nbsp;·&nbsp; Encaissement avance → D&nbsp;512 / C&nbsp;519 &nbsp;·&nbsp; Agios → D&nbsp;661 / C&nbsp;512 &nbsp;·&nbsp; Retour (impayé) → D&nbsp;411 / C&nbsp;519 + D&nbsp;519 / C&nbsp;512
      </div>

      {/* Modal remise */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
          <div className="card" style={{ width: "100%", maxWidth: 520 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{editTarget ? "Modifier la remise" : "Nouvelle remise à l'escompte"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Réf. facture</label>
                  <input className="input" value={form.invoice_ref} onChange={e => handleFormChange("invoice_ref", e.target.value)} placeholder="FA-2024-001" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Banque</label>
                  <input className="input" value={form.bank_name} onChange={e => handleFormChange("bank_name", e.target.value)} placeholder="Crédit Agricole…" />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom du client (débiteur)</label>
                  <input className="input" value={form.client_name} onChange={e => handleFormChange("client_name", e.target.value)} placeholder="Société débitrice" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Valeur nominale (€) *</label>
                  <input className="input" type="number" step="0.01" required value={form.face_value} onChange={e => handleFormChange("face_value", e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Taux d'escompte (%) *</label>
                  <input className="input" type="number" step="0.01" required value={form.discount_rate} onChange={e => handleFormChange("discount_rate", e.target.value)} placeholder="3.50" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Commissions bancaires (€)</label>
                  <input className="input" type="number" step="0.01" value={form.commission_fees} onChange={e => handleFormChange("commission_fees", e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de remise *</label>
                  <input className="input" type="date" required value={form.remittance_date} onChange={e => handleFormChange("remittance_date", e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date d'échéance *</label>
                  <input className="input" type="date" required value={form.due_date} onChange={e => handleFormChange("due_date", e.target.value)} />
                </div>
              </div>
              {preview && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                  {[["Durée", `${preview.days} jours`], ["Agios", `${fmt(preview.agios)} €`], ["Net reçu", `${fmt(preview.net)} €`], ["TEG", `${preview.teg}%`]].map(([l, v]) => (
                    <div key={l} style={{ background: "#f9fafb", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "…" : editTarget ? "Modifier" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Périodes & Clôtures
// ════════════════════════════════════════════════════════════════════════
const STATUS_COLOR = { ouvert: "#1a9e72", en_cours: "#f59e0b", "clôturé": "#e24b4a", "verrouillé": "#7c3aed" };
const STATUS_ICON  = { ouvert: "🟢", en_cours: "🟡", "clôturé": "🔴", "verrouillé": "🔒" };

function PeriodesTab({ currentCompanyId }) {
  const [periods, setPeriods]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showWizard, setShowWizard] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardChecks, setWizardChecks] = useState({});
  const [msg, setMsg]             = useState(null);
  const [form, setForm]           = useState({ period_type: "mensuel", start_date: "", end_date: "", fiscal_year: String(new Date().getFullYear()) });
  const [unlockForm, setUnlockForm] = useState({ id: null, reason: "" });

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.periods.list().then(r => setPeriods(r.periods || [])).catch(console.error).finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const notify = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.periods.create(form);
      setShowCreate(false);
      load();
      notify("ok", "Période créée.");
    } catch (err) { notify("error", err.message); }
  };

  const startWizard = async (period) => {
    setShowWizard(period);
    setWizardStep(0);
    setWizardChecks({});
    try {
      const bc = await api.periods.balanceCheck(period.id);
      setWizardChecks(bc);
    } catch (e) { notify("error", e.message); }
  };

  const handleClose = async () => {
    try {
      await api.periods.close(showWizard.id);
      setShowWizard(null);
      load();
      notify("ok", "Période clôturée avec succès.");
    } catch (err) { notify("error", err.message); }
  };

  const handleLock = async (id) => {
    if (!window.confirm("Verrouiller définitivement cette période ? Aucune modification ne sera possible.")) return;
    try { await api.periods.lock(id); load(); notify("ok", "Période verrouillée."); }
    catch (err) { notify("error", err.message); }
  };

  const handleUnlock = async () => {
    if (unlockForm.reason.trim().length < 10) { notify("error", "Raison obligatoire (minimum 10 caractères)."); return; }
    try {
      await api.periods.unlock(unlockForm.id, { unlock_reason: unlockForm.reason });
      setUnlockForm({ id: null, reason: "" });
      load(); notify("ok", "Période déverrouillée.");
    } catch (err) { notify("error", err.message); }
  };

  const wizardOk = wizardChecks.is_balanced && wizardChecks.unaccounted_invoices === 0;

  return (
    <div>
      {msg && (
        <div style={{ marginBottom: 12, padding: "8px 14px", borderRadius: 8, fontSize: 13,
          background: msg.type === "ok" ? "#dcfce7" : "#fee2e2", color: msg.type === "ok" ? "#166534" : "#991b1b" }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <button className="btn primary" onClick={() => setShowCreate(true)}>+ Nouvelle période</button>
        <button className="btn" onClick={load}>🔄 Actualiser</button>
      </div>

      {/* Tableau périodes */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              {["Type","Début","Fin","Exercice","Statut","Débit","Crédit","Équilibré","Actions"].map(h => (
                <th key={h} style={{ padding: "8px 12px", fontWeight: 600, textAlign: h === "Actions" ? "center" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: 32 }}>Chargement…</td></tr>
            ) : periods.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🗓️</div>Aucune période créée
              </td></tr>
            ) : periods.map(p => (
              <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                <td style={{ padding: "9px 12px" }}>{p.period_type}</td>
                <td style={{ padding: "9px 12px", fontFamily: "monospace", fontSize: 12 }}>{p.start_date}</td>
                <td style={{ padding: "9px 12px", fontFamily: "monospace", fontSize: 12 }}>{p.end_date}</td>
                <td style={{ padding: "9px 12px", fontWeight: 700 }}>{p.fiscal_year}</td>
                <td style={{ padding: "9px 12px" }}>
                  <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, fontWeight: 700,
                    background: (STATUS_COLOR[p.status] || "#999") + "18", color: STATUS_COLOR[p.status] || "#999" }}>
                    {STATUS_ICON[p.status]} {p.status}
                  </span>
                </td>
                <td style={{ padding: "9px 12px", textAlign: "right", color: "#e24b4a" }}>{p.total_debit ? fmt(p.total_debit) + " €" : "—"}</td>
                <td style={{ padding: "9px 12px", textAlign: "right", color: "#1a9e72" }}>{p.total_credit ? fmt(p.total_credit) + " €" : "—"}</td>
                <td style={{ padding: "9px 12px", textAlign: "center" }}>{p.is_balanced ? "✅" : p.status === "ouvert" ? "—" : "⚠️"}</td>
                <td style={{ padding: "9px 12px" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                    {p.status === "ouvert" && (
                      <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => startWizard(p)}>🔒 Clôturer</button>
                    )}
                    {p.status === "clôturé" && (
                      <button className="btn" style={{ fontSize: 11, color: "#7c3aed" }} onClick={() => handleLock(p.id)}>🔐 Verrouiller</button>
                    )}
                    {p.status === "verrouillé" && (
                      <button className="btn" style={{ fontSize: 11, color: "#f59e0b" }} onClick={() => setUnlockForm({ id: p.id, reason: "" })}>🔓 Déverrouiller</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modale création */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Créer une période</div>
            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Type *</label>
                  <select className="input" value={form.period_type} onChange={e => setForm(p => ({ ...p, period_type: e.target.value }))}>
                    <option value="mensuel">Mensuel</option>
                    <option value="trimestriel">Trimestriel</option>
                    <option value="annuel">Annuel</option>
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Début *</label>
                    <input className="input" type="date" required value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Fin *</label>
                    <input className="input" type="date" required value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Exercice fiscal *</label>
                  <input className="input" type="number" required value={form.fiscal_year} onChange={e => setForm(p => ({ ...p, fiscal_year: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="submit" className="btn primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Wizard clôture */}
      {showWizard && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: 520, padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Clôture de période</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>{showWizard.start_date} → {showWizard.end_date}</div>

            {/* Étapes de vérification */}
            {[
              { label: "Balance équilibrée", ok: wizardChecks.is_balanced, detail: wizardChecks.ecart > 0 ? `Écart : ${wizardChecks.ecart?.toFixed(2)} €` : null },
              { label: "Toutes factures comptabilisées", ok: wizardChecks.unaccounted_invoices === 0, detail: wizardChecks.unaccounted_invoices > 0 ? `${wizardChecks.unaccounted_invoices} facture(s) sans écriture` : null },
              { label: "Aucune écriture en suspens", ok: true },
              { label: "Données prêtes pour archivage", ok: true },
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <span style={{ fontSize: 16 }}>{step.ok == null ? "⏳" : step.ok ? "✅" : "❌"}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{step.label}</div>
                  {step.detail && <div style={{ fontSize: 11, color: "#e24b4a", marginTop: 2 }}>{step.detail}</div>}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setShowWizard(null)}>Annuler</button>
              <button className="btn primary" disabled={!wizardOk} onClick={handleClose}
                style={{ opacity: wizardOk ? 1 : 0.5 }}>
                {wizardOk ? "✅ Confirmer la clôture" : "Résolvez les erreurs d'abord"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale déverrouillage */}
      {unlockForm.id && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: 440, padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>⚠️ Déverrouillage de période</div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
              Cette action est enregistrée dans le journal d'audit et notifiée à tous les administrateurs.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Raison du déverrouillage (obligatoire) *</label>
            <textarea className="input" rows={3} style={{ marginTop: 6, resize: "vertical" }}
              placeholder="Minimum 10 caractères…"
              value={unlockForm.reason}
              onChange={e => setUnlockForm(p => ({ ...p, reason: e.target.value }))} />
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setUnlockForm({ id: null, reason: "" })}>Annuler</button>
              <button className="btn" style={{ background: "#f59e0b", color: "white" }} onClick={handleUnlock}>🔓 Déverrouiller</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Provisions & Régularisations
// ════════════════════════════════════════════════════════════════════════
const ACCRUAL_TYPES = {
  cca: "CCA (Charges constatées d'avance)",
  pca: "PCA (Produits constatés d'avance)",
  provision_creance: "Provision créance douteuse",
  provision_risque: "Provision pour risque",
  charge_payer: "Charge à payer",
  produit_recevoir: "Produit à recevoir",
};
const PROVISION_ACCOUNTS = [
  { code: "151", label: "151 — Provisions risques" },
  { code: "152", label: "152 — Provisions charges" },
  { code: "486", label: "486 — CCA" },
  { code: "487", label: "487 — PCA" },
  { code: "491", label: "491 — Provisions créances douteuses" },
  { code: "408", label: "408 — Fournisseurs — FNP" },
  { code: "418", label: "418 — Clients — PNF" },
  { code: "6811", label: "6811 — Dotations amortissements" },
  { code: "6815", label: "6815 — Dotations provisions" },
  { code: "6817", label: "6817 — Dotations créances" },
  { code: "7815", label: "7815 — Reprises provisions" },
  { code: "7817", label: "7817 — Reprises créances" },
  { code: "411", label: "411 — Clients" },
  { code: "401", label: "401 — Fournisseurs" },
  { code: "706", label: "706 — Prestations" },
  { code: "607", label: "607 — Achats" },
  { code: "512", label: "512 — Banque" },
];

function ProvisionsTab({ currentCompanyId }) {
  const [accruals, setAccruals]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg]             = useState(null);
  const [filterStatus, setFilterStatus] = useState("actif");
  const [form, setForm] = useState({
    type: "provision_creance", description: "", amount: "",
    start_date: new Date().toISOString().slice(0, 10), end_date: "",
    account_debit: "491", account_credit: "411",
  });

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.accruals.list({ status: filterStatus || undefined })
      .then(r => setAccruals(r.accruals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const notify = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.accruals.create({ ...form, amount: parseFloat(form.amount) });
      setShowCreate(false);
      load(); notify("ok", "Provision créée et écriture générée.");
    } catch (err) { notify("error", err.message); }
  };

  const handleReverse = async (a) => {
    if (!window.confirm(`Contre-passer "${a.description}" ? Cette action génère une écriture inverse.`)) return;
    try {
      await api.accruals.reverse(a.id);
      load(); notify("ok", "Contre-passation effectuée.");
    } catch (err) { notify("error", err.message); }
  };

  const handleCancel = async (a) => {
    if (!window.confirm(`Annuler "${a.description}" ?`)) return;
    try {
      await api.accruals.cancel(a.id);
      load(); notify("ok", "Provision annulée.");
    } catch (err) { notify("error", err.message); }
  };

  const totalActif = accruals.filter(a => a.status === "actif").reduce((s, a) => s + a.amount, 0);

  return (
    <div>
      {msg && (
        <div style={{ marginBottom: 12, padding: "8px 14px", borderRadius: 8, fontSize: 13,
          background: msg.type === "ok" ? "#dcfce7" : "#fee2e2", color: msg.type === "ok" ? "#166534" : "#991b1b" }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <button className="btn primary" onClick={() => setShowCreate(true)}>+ Nouvelle provision</button>
        {["actif","contre-passé","annulé"].map(s => (
          <button key={s} className={`btn${filterStatus === s ? " primary" : ""}`} onClick={() => setFilterStatus(s)}>{s}</button>
        ))}
        <button className="btn" onClick={() => { setFilterStatus(""); load(); }}>Toutes</button>
        {totalActif > 0 && <span style={{ fontSize: 13, marginLeft: "auto", color: "#e24b4a", fontWeight: 700 }}>Total actif : {fmt(totalActif)} €</span>}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              {["Type","Description","Montant","Date début","Compte D","Compte C","Statut","Actions"].map(h => (
                <th key={h} style={{ padding: "8px 12px", fontWeight: 600, textAlign: h === "Montant" ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 32 }}>Chargement…</td></tr>
            : accruals.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Aucune provision</td></tr>
            : accruals.map(a => (
              <tr key={a.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 12 }}>
                <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 10, background: "#e0e7ff", color: "#3730a3", padding: "2px 7px", borderRadius: 8 }}>{a.type}</span></td>
                <td style={{ padding: "8px 12px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.description}</td>
                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#e24b4a" }}>{fmt(a.amount)} €</td>
                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11 }}>{a.start_date}</td>
                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11, color: "#185fa5" }}>{a.account_debit}</td>
                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11, color: "#1a9e72" }}>{a.account_credit}</td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 700,
                    background: a.status === "actif" ? "#dcfce7" : a.status === "contre-passé" ? "#fef3c7" : "#fee2e2",
                    color: a.status === "actif" ? "#166534" : a.status === "contre-passé" ? "#92400e" : "#991b1b" }}>
                    {a.status}
                  </span>
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {a.status === "actif" && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn" style={{ fontSize: 10 }} onClick={() => handleReverse(a)}>↩️ Contre-passer</button>
                      <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleCancel(a)}>✕</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modale création */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: 500, padding: 24, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Nouvelle provision / régularisation</div>
            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Type *</label>
                  <select className="input" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    {Object.entries(ACCRUAL_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Description *</label>
                  <input className="input" required value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Montant (€) *</label>
                  <input className="input" type="number" step="0.01" min="0.01" max="10000000" required value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Date début *</label>
                    <input className="input" type="date" required value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Date fin</label>
                    <input className="input" type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Compte débit *</label>
                    <select className="input" value={form.account_debit} onChange={e => setForm(p => ({ ...p, account_debit: e.target.value }))}>
                      {PROVISION_ACCOUNTS.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Compte crédit *</label>
                    <select className="input" value={form.account_credit} onChange={e => setForm(p => ({ ...p, account_credit: e.target.value }))}>
                      {PROVISION_ACCOUNTS.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="submit" className="btn primary">Créer + comptabiliser</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Santé comptable
// ════════════════════════════════════════════════════════════════════════
function SanteTab({ currentCompanyId }) {
  const [health, setHealth]       = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [running, setRunning]     = useState(false);
  const [msg, setMsg]             = useState(null);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.accounting.health().catch(() => null),
      api.accounting.healthHistory(30).catch(() => []),
    ]).then(([h, hist]) => {
      setHealth(h);
      setHistory(Array.isArray(hist) ? hist : []);
    }).finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await api.accounting.healthRunNow();
      setHealth(r);
      setMsg({ type: "ok", text: `Contrôle effectué — Score : ${r.score}/100` });
      load();
    } catch (err) { setMsg({ type: "error", text: err.message }); }
    finally { setRunning(false); }
  };

  const scoreColor = (s) => s >= 90 ? "#1a9e72" : s >= 70 ? "#f59e0b" : s >= 50 ? "#f97316" : "#e24b4a";
  const scoreLabel = (s) => s >= 90 ? "Excellent 🟢" : s >= 70 ? "Attention 🟡" : s >= 50 ? "Problèmes 🟠" : "CRITIQUE 🔴";

  const issues = health?.issues || [];

  return (
    <div>
      {msg && (
        <div style={{ marginBottom: 12, padding: "8px 14px", borderRadius: 8, fontSize: 13,
          background: msg.type === "ok" ? "#dcfce7" : "#fee2e2", color: msg.type === "ok" ? "#166534" : "#991b1b" }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ float: "right", border: "none", background: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "flex-start" }}>
        {/* Score */}
        <div>
          <div className="card" style={{ textAlign: "center", padding: 28 }}>
            {loading ? <div style={{ color: "var(--text-muted)" }}>Chargement…</div> : health === null ? (
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucun contrôle effectué.<br />Lancez le premier contrôle.</div>
            ) : (
              <>
                <div style={{ fontSize: 52, fontWeight: 900, color: scoreColor(health.score), lineHeight: 1 }}>{health.score}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 2px" }}>/ 100</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: scoreColor(health.score), marginBottom: 12 }}>{scoreLabel(health.score)}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Au {fmtDate(health.check_date)}</div>
              </>
            )}
            <button className="btn primary" style={{ marginTop: 16, width: "100%" }} onClick={handleRunNow} disabled={running}>
              {running ? "⏳ Contrôle en cours…" : "🔍 Lancer un contrôle"}
            </button>
          </div>

          {/* Historique scores */}
          {history.length > 0 && (
            <div className="card" style={{ marginTop: 12, padding: "12px 16px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: "var(--text-muted)" }}>HISTORIQUE (30 jours)</div>
              {history.slice(0, 10).map(h => (
                <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>
                  <span style={{ color: "var(--text-muted)" }}>{fmtDate(h.check_date)}</span>
                  <span style={{ fontWeight: 700, color: scoreColor(h.score) }}>{h.score}/100</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Détail des contrôles */}
        <div>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Détail des contrôles</div>
            {[
              { code: "BALANCE_DESEQUILIBREE",    label: "Balance équilibrée",                weight: 30 },
              { code: "FACTURES_SANS_ECRITURE",   label: "Toutes factures comptabilisées",    weight: 20 },
              { code: "COMPTE_ATTENTE_NON_NUL",   label: "Comptes d'attente (471/472) soldés",weight: 15 },
              { code: "ECART_TVA",                label: "Cohérence TVA (44571)",             weight: 10 },
              { code: "ECART_BANCAIRE",           label: "Rapprochement bancaire (512)",      weight: 10 },
            ].map(ctrl => {
              const issue = issues.find(i => i.code === ctrl.code);
              return (
                <div key={ctrl.code} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{issue ? "❌" : health ? "✅" : "⏳"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ctrl.label}</div>
                    {issue && <div style={{ fontSize: 12, color: "#e24b4a", marginTop: 2 }}>{issue.message}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0, textAlign: "right" }}>−{ctrl.weight} pts<br/>si échoué</div>
                </div>
              );
            })}
          </div>

          {issues.length === 0 && health && (
            <div style={{ marginTop: 12, padding: "12px 16px", background: "#dcfce7", borderRadius: 10, fontSize: 13, color: "#166534", fontWeight: 600 }}>
              ✅ Aucun problème détecté — Votre comptabilité est en parfaite santé !
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main Comptabilite component
// ════════════════════════════════════════════════════════════════════════
export default function Comptabilite({ currentCompanyId }) {
  const [tab, setTab]               = useState("journal");
  const [transactions, setTxs]      = useState([]);
  const [balance, setBalance]       = useState([]);
  const [txFilter, setTxFilter]     = useState("");
  const [txYear, setTxYear]         = useState(String(new Date().getFullYear()));
  const [loading, setLoading]       = useState(false);
  const [exportingFec, setExportingFec] = useState(false);

  const loadJournal = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.transactions.list({ company_id: currentCompanyId, year: txYear || undefined })
      .then(r => setTxs(Array.isArray(r) ? r : (r?.transactions || [])))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, txYear]);

  const loadBalance = useCallback(() => {
    if (!currentCompanyId) return;
    api.transactions.balance(currentCompanyId)
      .then(r => {
        const arr = Array.isArray(r) ? r : (r?.accounts || r?.balance || []);
        setBalance(arr);
      })
      .catch(console.error);
  }, [currentCompanyId]);

  useEffect(() => {
    if (tab === "journal") loadJournal();
    if (tab === "balance" || tab === "bilan") loadBalance();
  }, [tab, loadJournal, loadBalance]);

  // Export FEC authentifié — remplace l'ancien <a href="/api/fec/export/...">
  // qui ne transmettait jamais le token JWT et échouait systématiquement en 401.
  const handleExportFEC = async () => {
    setExportingFec(true);
    try {
      const { blob, filename } = await api.fec.exportDownload(currentCompanyId, txYear);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: filename }).click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setExportingFec(false);
    }
  };

  if (!currentCompanyId) {
    return (
      <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📒</div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Sélectionnez une société</div>
      </div>
    );
  }

  // Computed balance metrics
  const revenue   = balance.filter(a => String(a.account_code || a.code || "").startsWith("7")).reduce((s, a) => s + Math.abs((a.credit_total || a.credit || 0) - (a.debit_total || a.debit || 0)), 0);
  const expenses  = balance.filter(a => String(a.account_code || a.code || "").startsWith("6")).reduce((s, a) => s + Math.abs((a.debit_total || a.debit || 0) - (a.credit_total || a.credit || 0)), 0);
  const netResult = revenue - expenses;
  const treasury  = balance.filter(a => String(a.account_code || a.code || "").startsWith("5")).reduce((s, a) => s + ((a.debit_total || a.debit || 0) - (a.credit_total || a.credit || 0)), 0);
  const clients   = balance.filter(a => String(a.account_code || a.code || "").startsWith("41")).reduce((s, a) => s + Math.max(0, (a.debit_total || a.debit || 0) - (a.credit_total || a.credit || 0)), 0);
  const suppliers = balance.filter(a => String(a.account_code || a.code || "").startsWith("40")).reduce((s, a) => s + Math.max(0, (a.credit_total || a.credit || 0) - (a.debit_total || a.debit || 0)), 0);

  const filteredTxs = transactions.filter(t => !txFilter || (t.description || "").toLowerCase().includes(txFilter.toLowerCase()) || (t.account_code || "").includes(txFilter));

  const years = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h2 className="page-title">Comptabilité</h2>
        <div className="btn-group">
          <button className="btn" onClick={handleExportFEC} disabled={exportingFec}>
            {exportingFec ? "⏳ Export…" : "⬇️ Export FEC"}
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--border-color, #e2e8f0)", marginBottom: 20, marginTop: 16, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "9px 16px", fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
            cursor: "pointer", border: "none", background: "transparent",
            color: tab === t.id ? "var(--g)" : "var(--text-muted)",
            borderBottom: `2px solid ${tab === t.id ? "var(--g)" : "transparent"}`,
            marginBottom: -2, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ── Journal ────────────────────────────────────────────── */}
      {tab === "journal" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <select className="input" style={{ maxWidth: 120 }} value={txYear} onChange={e => setTxYear(e.target.value)}>
              <option value="">Toutes années</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <input className="input" placeholder="Filtrer par compte ou libellé…" value={txFilter} onChange={e => setTxFilter(e.target.value)} style={{ maxWidth: 280 }} />
            <button className="btn" onClick={loadJournal}>🔄 Actualiser</button>
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ display: "grid", gridTemplateColumns: "90px 120px 1fr 110px 110px", gap: 0, padding: "7px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", background: "#f9fafb", borderRadius: "8px 8px 0 0" }}>
                <span>Date</span><span>Compte</span><span>Libellé</span><span style={{ textAlign: "right" }}>Débit</span><span style={{ textAlign: "right" }}>Crédit</span>
              </div>
              {filteredTxs.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>Aucune écriture</div>
              ) : filteredTxs.slice(0, 200).map((t, i) => (
                <div key={t.id || i} style={{ display: "grid", gridTemplateColumns: "90px 120px 1fr 110px 110px", gap: 0, padding: "6px 12px", fontSize: 12, borderBottom: "0.5px solid #f3f4f6" }}>
                  <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{fmtDate(t.date || t.transaction_date)}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: "#185fa5" }}>{t.account_code || t.account || "—"}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.description || t.label || "—"}</span>
                  <span style={{ textAlign: "right", fontWeight: 600, color: "#e24b4a" }}>{t.debit || t.debit_amount ? fmt(t.debit || t.debit_amount) + " €" : ""}</span>
                  <span style={{ textAlign: "right", fontWeight: 600, color: "#1a9e72" }}>{t.credit || t.credit_amount ? fmt(t.credit || t.credit_amount) + " €" : ""}</span>
                </div>
              ))}
              {filteredTxs.length > 200 && (
                <div style={{ textAlign: "center", padding: "8px 0", fontSize: 12, color: "var(--text-muted)", borderTop: "var(--border)" }}>
                  … {filteredTxs.length - 200} écritures supplémentaires. Affinez le filtre.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Balance ────────────────────────────────────────────── */}
      {tab === "balance" && (
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                <th style={{ padding: "9px 14px", textAlign: "left" }}>Compte</th>
                <th style={{ padding: "9px 14px", textAlign: "left" }}>Intitulé</th>
                <th style={{ padding: "9px 14px", textAlign: "right" }}>Débit cumulé</th>
                <th style={{ padding: "9px 14px", textAlign: "right" }}>Crédit cumulé</th>
                <th style={{ padding: "9px 14px", textAlign: "right" }}>Solde débiteur</th>
                <th style={{ padding: "9px 14px", textAlign: "right" }}>Solde créditeur</th>
              </tr>
            </thead>
            <tbody>
              {balance.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>Aucune donnée de balance disponible</td></tr>
              ) : balance.map((a, i) => {
                const code = a.account_code || a.code || "";
                const deb  = a.debit_total || a.debit || 0;
                const cred = a.credit_total || a.credit || 0;
                const sd   = Math.max(0, deb - cred);
                const sc   = Math.max(0, cred - deb);
                return (
                  <tr key={code || i} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                    <td style={{ padding: "8px 14px", fontFamily: "monospace", fontWeight: 700, color: "#185fa5" }}>{code}</td>
                    <td style={{ padding: "8px 14px" }}>{a.account_label || a.label || a.name || "—"}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--text-muted)" }}>{fmt(deb)} €</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--text-muted)" }}>{fmt(cred)} €</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: "#e24b4a" }}>{sd > 0 ? fmt(sd) + " €" : "—"}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: "#1a9e72" }}>{sc > 0 ? fmt(sc) + " €" : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Bilan & P&L ───────────────────────────────────────── */}
      {tab === "bilan" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">📈 Compte de résultat (YTD)</span></div>
            {[
              ["Chiffre d'affaires (70x)", revenue, "#1a9e72"],
              ["Charges d'exploitation (60-65)", -expenses, "#e24b4a"],
              ["Résultat net", netResult, netResult >= 0 ? "#1a9e72" : "#e24b4a"],
            ].map(([l, v, c]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "0.5px solid #f3f4f6", fontSize: 13, fontWeight: l.includes("Résultat") ? 700 : 400 }}>
                <span style={{ color: "var(--text-muted)" }}>{l}</span>
                <span style={{ fontWeight: 700, color: c }}>{v >= 0 ? "+" : ""}{fmt(v)} €</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">📋 Bilan simplifié</span></div>
            <div style={{ fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: 1 }}>ACTIF</div>
            {[
              ["Trésorerie (512, 531)", treasury],
              ["Créances clients (411)", clients],
              ["Immobilisations (2xx)", balance.filter(a => String(a.account_code || a.code || "").startsWith("2")).reduce((s, a) => s + Math.max(0, (a.debit_total || a.debit || 0) - (a.credit_total || a.credit || 0)), 0)],
            ].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "0.5px solid #f3f4f6", fontSize: 13 }}>
                <span style={{ color: "var(--text-muted)" }}>{l}</span>
                <span style={{ fontWeight: 600 }}>{fmt(v)} €</span>
              </div>
            ))}
            <div style={{ fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", margin: "14px 0 8px", letterSpacing: 1 }}>PASSIF</div>
            {[
              ["Capital (101)", balance.filter(a => String(a.account_code || a.code || "").startsWith("101")).reduce((s, a) => s + Math.max(0, (a.credit_total || a.credit || 0) - (a.debit_total || a.debit || 0)), 0)],
              ["Résultat net", netResult],
              ["Dettes fournisseurs (401)", suppliers],
              ["Emprunts (16x)", balance.filter(a => String(a.account_code || a.code || "").startsWith("16")).reduce((s, a) => s + Math.max(0, (a.credit_total || a.credit || 0) - (a.debit_total || a.debit || 0)), 0)],
            ].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "0.5px solid #f3f4f6", fontSize: 13 }}>
                <span style={{ color: "var(--text-muted)" }}>{l}</span>
                <span style={{ fontWeight: 600, color: l.includes("Résultat") ? (v >= 0 ? "#1a9e72" : "#e24b4a") : "inherit" }}>{fmt(v)} €</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Grand livre ────────────────────────────────────────── */}
      {tab === "grand-livre" && <GrandLivreTab currentCompanyId={currentCompanyId} />}

      {/* ── Lettrage ───────────────────────────────────────────── */}
      {tab === "lettrage" && <LettrageTab currentCompanyId={currentCompanyId} />}

      {/* ── Balance âgée ───────────────────────────────────────── */}
      {tab === "balance-agee" && <BalanceAgeeTab currentCompanyId={currentCompanyId} />}

      {/* ── Périodes & Clôtures ────────────────────────────────── */}
      {tab === "periodes" && <PeriodesTab currentCompanyId={currentCompanyId} />}

      {/* ── Provisions & Régularisations ───────────────────────── */}
      {tab === "provisions" && <ProvisionsTab currentCompanyId={currentCompanyId} />}

      {/* ── Santé comptable ─────────────────────────────────────── */}
      {tab === "sante" && <SanteTab currentCompanyId={currentCompanyId} />}

      {/* ── CCA ───────────────────────────────────────────────── */}
      {tab === "cca" && <CCATab currentCompanyId={currentCompanyId} />}

      {/* ── Prêts ─────────────────────────────────────────────── */}
      {tab === "loans" && <LoansTab currentCompanyId={currentCompanyId} />}

      {/* ── Escomptes commerciaux ─────────────────────────────── */}
      {tab === "discounts" && <DiscountsTab currentCompanyId={currentCompanyId} />}

      {/* ── Escompte bancaire ─────────────────────────────────── */}
      {tab === "bank-discounting" && <BankDiscountingTab currentCompanyId={currentCompanyId} />}
    </div>
  );
}
